import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Plus, 
    Search as SearchIcon, 
    Edit, 
    Trash2, 
    Hospital,
    MapPin,
    Users,
    CheckCircle2,
    Settings2,
    X,
    Save,
    ChevronLeft,
    ChevronRight,
    ArrowUp,
    ArrowDown
} from 'lucide-react';
import { AuthContext } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { API_BASE_URL } from '../../../lib/constants';

const OTRooms = () => {
    const { userRole, permissions } = useContext(AuthContext);
    const { showNotification } = useNotification();

    const [otRooms, setOtRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [sortConfig, setSortConfig] = useState({ key: 'room_number', direction: 'asc' });
    const currentPage = parseInt(searchParams.get('page') || '0', 10);
    const ROWS_PER_PAGE = 10;

    // Form states
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState({
        room_number: '',
        room_name: '',
        status: 'Active',
        capacity: ''
    });

    const isAuthorized = userRole === 'Admin' || permissions?.includes('OT Rooms Edit') || permissions?.includes('OT Rooms Add');
    const canDelete = userRole === 'Admin' || permissions?.includes('OT Rooms Delete');
    const showActionsColumn = isAuthorized || canDelete;

    useEffect(() => {
        fetchOtRooms();
    }, []);

    const fetchOtRooms = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/get_ot_rooms`, {
                withCredentials: true
            });
            if (response.data.status === 'success') {
                setOtRooms(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching OT rooms:', error);
            showNotification('error', 'Failed to fetch Operation Theatres');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openAddForm = () => {
        setFormData({
            room_number: '',
            room_name: '',
            status: 'Active',
            capacity: ''
        });
        setEditingRoom(null);
        setIsFormOpen(true);
    };

    const openEditForm = (room) => {
        setFormData({
            room_number: room.room_number || '',
            room_name: room.room_name || '',
            status: room.status || 'Active',
            capacity: room.capacity || ''
        });
        setEditingRoom(room);
        setIsFormOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingRoom) {
                // Update
                const response = await axios.put(`${API_BASE_URL}/update_ot_room/${editingRoom.id}`, formData, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre updated successfully');
                    setIsFormOpen(false);
                    fetchOtRooms();
                }
            } else {
                // Add
                const response = await axios.post(`${API_BASE_URL}/add_ot_room`, formData, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre added successfully');
                    setIsFormOpen(false);
                    fetchOtRooms();
                }
            }
        } catch (error) {
            console.error('Error saving OT room:', error);
            showNotification('error', error.response?.data?.message || 'Failed to save Operation Theatre details');
        }
    };

    const handleDelete = async (id, roomNumber) => {
        if (window.confirm(`Are you sure you want to delete OT Room - ${roomNumber}?`)) {
            try {
                const response = await axios.delete(`${API_BASE_URL}/delete_ot_room/${id}`, {
                    withCredentials: true
                });
                if (response.data.status === 'success') {
                    showNotification('success', 'Operation Theatre deleted successfully');
                    fetchOtRooms();
                }
            } catch (error) {
                console.error('Error deleting OT room:', error);
                showNotification('error', 'Failed to delete Operation Theatre');
            }
        }
    };

    const filteredRooms = otRooms.filter(room =>
        room.room_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        room.room_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply Sorting
    filteredRooms.sort((a, b) => {
        if (!sortConfig.key) return 0;
        const aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        const bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        
        if (sortConfig.key === 'capacity') {
            const numA = parseInt(aVal) || 0;
            const numB = parseInt(bVal) || 0;
            return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.ceil(filteredRooms.length / ROWS_PER_PAGE);
    const paginatedRooms = filteredRooms.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);
    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: isDesktop ? '32px' : '16px' }}>
            
            {/* Unified Action Row: Search | Add */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
                    <SearchIcon style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, color: '#94a3b8' }} />
                    <input
                        type="text"
                        placeholder="Search OT rooms..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSearchParams({ page: '0' });
                        }}
                        style={{
                            width: '100%', padding: '12px 14px 12px 44px', border: '1px solid #e2e8f0',
                            borderRadius: '14px', fontSize: '15px', outline: 'none',
                            background: '#fff', color: '#1e293b', boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                            boxSizing: 'border-box'
                        }}
                    />
                    {searchTerm && (
                        <button 
                            onClick={() => { setSearchTerm(''); setSearchParams({ page: '0' }); }} 
                            style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                        >
                            <X style={{ width: 16, height: 16 }} />
                        </button>
                    )}
                </div>

                {isAuthorized && (
                    <button
                        onClick={openAddForm}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px',
                            background: '#10b981', color: '#fff', border: 'none', borderRadius: '12px',
                            fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(16,185,129,0.2)',
                            transition: 'all 0.2s', whiteSpace: 'nowrap'
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                        <Plus style={{ width: 18, height: 18 }} /> Add OT Room
                    </button>
                )}
            </div>

            {/* Content Area */}
            {loading ? (
                <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[...Array(5)].map((_, i) => (
                        <div key={i} style={{ height: 75, background: '#fff', border: '1px solid #f1f5f9', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
                    ))}
                </div>
            ) : filteredRooms.length === 0 ? (
                <div style={{ background: '#fff', borderRadius: '24px', border: '1px solid #e2e8f0', padding: '80px 24px', textAlign: 'center' }}>
                    <div style={{ width: 64, height: 64, borderRadius: '20px', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                        <Hospital style={{ width: 32, height: 32, color: '#cbd5e1' }} />
                    </div>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#475569', fontWeight: 700 }}>No operating theatres found</h3>
                    <p style={{ margin: '8px 0 24px', color: '#94a3b8', fontSize: '14px' }}>
                        {searchTerm ? "Try adjusting your search filters" : "Start by adding your hospital's operation theatre details"}
                    </p>
                    {isAuthorized && (
                        <button 
                            onClick={openAddForm}
                            style={{ 
                                background: '#10b981', color: '#fff', border: 'none', borderRadius: '12px', 
                                padding: '12px 28px', cursor: 'pointer', fontWeight: 700, fontSize: '15px', 
                                boxShadow: '0 4px 12px rgba(16,185,129,0.2)' 
                            }}
                        >
                            Add Your First Room
                        </button>
                    )}
                </div>
            ) : isDesktop ? (
                /* TABLE VIEW FOR DESKTOP */
                <div style={{ background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {[
                                    { label: 'Room Identification', key: 'room_number' },
                                    { label: 'Capacity', key: 'capacity' },
                                    { label: 'Status', key: 'status' }
                                ].map(h => (
                                    <th 
                                        key={h.key} 
                                        onClick={() => requestSort(h.key)}
                                        style={{ 
                                            padding: '18px 24px', fontSize: '12px', fontWeight: 700, color: '#64748b', 
                                            textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer' 
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            {h.label}
                                            {sortConfig.key === h.key && (
                                                sortConfig.direction === 'asc' ? <ArrowUp style={{ width: 12, height: 12 }} /> : <ArrowDown style={{ width: 12, height: 12 }} />
                                            )}
                                        </div>
                                    </th>
                                ))}
                                {showActionsColumn && (
                                    <th style={{ padding: '18px 24px', fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedRooms.map(room => (
                                <tr key={room.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#fcfdfe'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                    <td style={{ padding: '20px 24px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                            <div style={{ 
                                                width: 44, height: 44, borderRadius: '12px', 
                                                background: room.status === 'Active' ? '#f0fdf4' : room.status === 'Maintenance' ? '#fffbeb' : '#f8fafc', 
                                                border: `1px solid ${room.status === 'Active' ? '#a7f3d0' : room.status === 'Maintenance' ? '#fde68a' : '#e2e8f0'}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                                            }}>
                                                <Hospital style={{ width: 22, height: 22, color: room.status === 'Active' ? '#10b981' : room.status === 'Maintenance' ? '#f59e0b' : '#64748b' }} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>{room.room_number}</div>
                                                <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '2px' }}>{room.room_name || 'Standard Surgery Unit'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '20px 24px' }}>
                                        <div style={{ display: 'flex', itemsCenter: 'center', gap: '8px' }}>
                                            <Users style={{ width: 16, height: 16, color: '#94a3b8' }} />
                                            <span style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>{room.capacity || '4'} Persons</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '20px 24px' }}>
                                        <span style={{ 
                                            padding: '6px 12px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em',
                                            background: room.status === 'Active' ? '#f0fdf4' : room.status === 'Maintenance' ? '#fffbeb' : '#f1f5f9',
                                            color: room.status === 'Active' ? '#10b981' : room.status === 'Maintenance' ? '#f59e0b' : '#64748b',
                                            border: `1px solid ${room.status === 'Active' ? '#dcfce7' : room.status === 'Maintenance' ? '#fef3c7' : '#e2e8f0'}`
                                        }}>
                                            {room.status}
                                        </span>
                                    </td>
                                    {showActionsColumn && (
                                        <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                                            <div className="flex gap-2 justify-end">
                                                {isAuthorized && (
                                                    <button 
                                                        onClick={() => openEditForm(room)}
                                                        className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium text-sm"
                                                        title="Edit Room"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                        Edit
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button 
                                                        onClick={() => handleDelete(room.id, room.room_number)}
                                                        className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium text-sm"
                                                        title="Delete Room"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* CARD VIEW FOR MOBILE */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {paginatedRooms.map(room => (
                        <div key={room.id} style={{ background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <div style={{ 
                                        width: 44, height: 44, borderRadius: '12px', 
                                        background: room.status === 'Active' ? '#f0fdf4' : '#fffbeb',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center' 
                                    }}>
                                        <Hospital style={{ width: 22, height: 22, color: room.status === 'Active' ? '#10b981' : '#f59e0b' }} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{room.room_number}</h3>
                                        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#94a3b8' }}>{room.room_name}</p>
                                    </div>
                                </div>
                                <span style={{ 
                                    padding: '4px 10px', borderRadius: '8px', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                                    background: room.status === 'Active' ? '#f0fdf4' : '#fffbeb',
                                    color: room.status === 'Active' ? '#10b981' : '#f59e0b'
                                }}>
                                    {room.status}
                                </span>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '12px', background: '#f8fafc', borderRadius: '12px', marginBottom: '16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Users style={{ width: 14, height: 14, color: '#94a3b8' }} />
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>{room.capacity} Cap.</span>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                {isAuthorized && (
                                    <button 
                                        onClick={() => openEditForm(room)}
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        <Edit className="h-4 w-4" /> Edit
                                    </button>
                                )}
                                {canDelete && (
                                    <button 
                                        onClick={() => handleDelete(room.id, room.room_number)}
                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg text-sm font-medium transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination / Info Bar */}
            <div style={{ 
                marginTop: '24px', background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', 
                padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                    Showing <strong>{filteredRooms.length > 0 ? currentPage * ROWS_PER_PAGE + 1 : 0}</strong> to <strong>{Math.min((currentPage + 1) * ROWS_PER_PAGE, filteredRooms.length)}</strong> of <strong>{filteredRooms.length}</strong> OT rooms
                </span>

                {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button 
                            disabled={currentPage === 0} 
                            onClick={() => setSearchParams({ page: (currentPage - 1).toString() })}
                            style={{
                                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', 
                                cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.5 : 1
                            }}
                        >
                            <ChevronLeft style={{ width: 18, height: 18 }} />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setSearchParams({ page: i.toString() })}
                                style={{
                                    width: 36, height: 36, border: '1px solid', 
                                    borderRadius: '10px', fontSize: '14px', fontWeight: 700,
                                    cursor: 'pointer',
                                    borderColor: currentPage === i ? '#10b981' : '#e2e8f0',
                                    background: currentPage === i ? '#10b981' : '#fff',
                                    color: currentPage === i ? '#fff' : '#64748b'
                                }}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button 
                            disabled={currentPage >= totalPages - 1} 
                            onClick={() => setSearchParams({ page: (currentPage + 1).toString() })}
                            style={{
                                width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1px solid #e2e8f0', borderRadius: '10px', background: '#fff', 
                                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.5 : 1
                            }}
                        >
                            <ChevronRight style={{ width: 18, height: 18 }} />
                        </button>
                    </div>
                )}
            </div>

            {/* Modal Form Overlay */}
            {isFormOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 bg-slate-50/50">
                            <h3 className="text-lg font-bold text-slate-800">
                                {editingRoom ? 'Edit Operation Theatre' : 'Add New Operation Theatre'}
                            </h3>
                            <button
                                onClick={() => setIsFormOpen(false)}
                                className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-xl hover:bg-slate-100"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">OT Room Number <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="room_number"
                                        required
                                        value={formData.room_number}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                        placeholder="e.g. OT-01, Room A"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Room Type/Name</label>
                                    <input
                                        type="text"
                                        name="room_name"
                                        value={formData.room_name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                        placeholder="e.g. Cardiac OT, Minor Surgical"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Status</label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium appearance-none"
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Maintenance">Maintenance</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Capacity</label>
                                        <input
                                            type="number"
                                            name="capacity"
                                            value={formData.capacity}
                                            onChange={handleInputChange}
                                            min="1"
                                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
                                            placeholder="e.g. 5"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsFormOpen(false)}
                                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 font-bold rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-all flex justify-center items-center gap-2 shadow-lg shadow-emerald-200 active:scale-[0.98]"
                                >
                                    <Save className="w-5 h-5" />
                                    {editingRoom ? 'Save Changes' : 'Create Room'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OTRooms;
